import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { CURRENCY_SYMBOLS } from "../currency";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

/**
 * Modal to manage which employees are assigned to a task and optionally
 * override the fixed compensation amount per assignment.
 *
 * Props:
 *   open          : bool
 *   task          : task object (for context display)
 *   currency      : sheet currency code
 *   allEmployees  : list of {id, name} employees on the project
 *   memberships   : list of {user_id, name, default_percentage} (for hint text)
 *   onClose       : () => void
 *   onSaved       : (newAssignments) => void
 */
export default function AssignmentsModal({
  open,
  task,
  currency = "USD",
  allEmployees = [],
  memberships = [],
  onClose,
  onSaved,
}) {
  // Local editable map: user_id -> { included, fixedAmount }
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const symbol = CURRENCY_SYMBOLS[currency] || "$";

  useEffect(() => {
    if (!open || !task) return;
    const map = {};
    for (const a of task.assignments || []) {
      map[a.user_id] = {
        included: true,
        fixedAmount: a.fixed_amount != null ? String(a.fixed_amount) : "",
      };
    }
    setRows(map);
    setErr(null);
    setBusy(false);
  }, [open, task]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !task) return null;

  const toggle = (uid) => {
    setRows((r) => ({
      ...r,
      [uid]: {
        included: !(r[uid]?.included),
        fixedAmount: r[uid]?.fixedAmount || "",
      },
    }));
  };

  const setAmt = (uid, value) => {
    setRows((r) => ({
      ...r,
      [uid]: {
        included: r[uid]?.included ?? false,
        fixedAmount: value,
      },
    }));
  };

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = Object.entries(rows)
        .filter(([, v]) => v.included)
        .map(([uid, v]) => ({
          user_id: Number(uid),
          fixed_amount:
            v.fixedAmount === "" || v.fixedAmount === null
              ? null
              : Number(v.fixedAmount),
        }));
      const { data } = await api.put(`/tasks/${task.id}/assignments`, payload);
      onSaved(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const membershipFor = (uid) => memberships.find((m) => m.user_id === uid);

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>Assignments</h3>
        <p className="muted" style={{ marginTop: 4, marginBottom: 16 }}>
          Task: <strong style={{ color: "var(--ink)" }}>{task.name}</strong>
        </p>

        {allEmployees.length === 0 ? (
          <div className="muted-sm">
            No employees on this project. Add employees on the project page first.
          </div>
        ) : (
          <div className="assignment-list">
            {allEmployees.map((emp) => {
              const row = rows[emp.id] || { included: false, fixedAmount: "" };
              const mem = membershipFor(emp.id);
              const pct = mem?.default_percentage;
              const hint = row.fixedAmount
                ? `Fixed ${symbol}${fmt(row.fixedAmount)}`
                : pct != null
                ? `Project rule: ${fmt(pct)}%`
                : "No pay defined";
              return (
                <div key={emp.id} className="assignment-row">
                  <label className="check-row" style={{ flex: 1 }}>
                    <input
                      type="checkbox"
                      className="cbx"
                      checked={row.included}
                      onChange={() => toggle(emp.id)}
                    />
                    <span style={{ fontWeight: 500 }}>{emp.name}</span>
                  </label>
                  <div className="assignment-hint muted-sm">{hint}</div>
                  <div className="amount-input-wrap">
                    <span className="amount-prefix">{symbol}</span>
                    <input
                      type="number"
                      step="1"
                      placeholder="fixed"
                      value={row.fixedAmount}
                      onChange={(e) => setAmt(emp.id, e.target.value)}
                      disabled={!row.included}
                      title="Optional fixed payout for this task (overrides project %)"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary" onClick={handleSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
